import type { ReactNode } from 'react';
import styles from './tile.module.scss';

export type TileContentProps = {
  /** Children to render in the content area */
  children: ReactNode;
  /** Additional class name */
  className?: string;
};

/**
 * TileContent - Main content area of a tile (wraps title, price, details, etc.)
 */
export function TileContent({ children, className }: TileContentProps) {
  return (
    <div className={`${styles.tileContent} ${className || ''}`}>{children}</div>
  );
}

export type TileTitleProps = {
  /** Title text */
  children: ReactNode;
  /** Additional class name */
  className?: string;
};

/**
 * TileTitle - Title/name of the tile item
 */
export function TileTitle({ children, className }: TileTitleProps) {
  const isLongTitle = typeof children === 'string' && children.length > 20;

  return (
    <h3
      className={`${styles.tileTitle} ${isLongTitle ? styles.tileTitleLong : ''} ${className || ''}`}
    >
      {children}
    </h3>
  );
}

export type TilePriceProps = {
  /** Price value (will be formatted) */
  price?: number | null;
  /** Min price for ranges */
  priceMin?: number | null;
  /** Max price for ranges */
  priceMax?: number | null;
  /** Custom price label (overrides price/priceMin/priceMax) */
  label?: string;
  /** Additional class name */
  className?: string;
};

const formatPrice = (price: number | null | undefined): string | null => {
  if (price === null || price === undefined) return null;
  return `$${price.toLocaleString()}`;
};

const formatPriceRange = (
  min: number | null | undefined,
  max: number | null | undefined
): string | null => {
  if (min === null && max === null) return null;
  if (min === null || min === undefined)
    return max ? `Up to $${max.toLocaleString()}` : null;
  if (max === null || max === undefined) return `From $${min.toLocaleString()}`;
  if (min === max) return `$${min.toLocaleString()}`;
  return `$${min.toLocaleString()} - $${max.toLocaleString()}`;
};

/**
 * TilePrice - Price display for tile
 */
export function TilePrice({
  price,
  priceMin,
  priceMax,
  label,
  className
}: TilePriceProps) {
  const priceLabel =
    label || formatPrice(price) || formatPriceRange(priceMin, priceMax);

  if (!priceLabel) return null;

  return (
    <p className={`${styles.tilePrice} ${className || ''}`}>{priceLabel}</p>
  );
}

export type TileDetailsProps = {
  /** Breed name */
  breed?: string | null;
  /** Gender (male/female) */
  gender?: 'male' | 'female' | null;
  /** Location city */
  city?: string | null;
  /** Location province/state abbreviated */
  province?: string | null;
  /** Estimated date for waitlists (e.g., "Early Jan 2025") */
  estimatedDate?: string | null;
  /** Paused label shown in red (for paused waitlists) */
  pausedLabel?: string | null;
  /** Custom detail items */
  children?: ReactNode;
  /** Additional class name */
  className?: string;
};

const formatGender = (
  gender: 'male' | 'female' | null | undefined
): string | null => {
  if (!gender) return null;
  return gender === 'male' ? 'Male' : 'Female';
};

const formatLocation = (
  city: string | null | undefined,
  province: string | null | undefined
): string | null => {
  if (!city && !province) return null;
  if (!city) return province ?? null;
  if (!province) return city;
  return `${city}, ${province}`;
};

/**
 * TileDetails - Secondary details (breed, gender, location, etc.)
 */
export function TileDetails({
  breed,
  gender,
  city,
  province,
  estimatedDate,
  pausedLabel,
  children,
  className
}: TileDetailsProps) {
  const genderLabel = formatGender(gender);
  const locationLabel = formatLocation(city, province);

  const hasDetails =
    breed ||
    genderLabel ||
    locationLabel ||
    estimatedDate ||
    pausedLabel ||
    children;
  if (!hasDetails) return null;

  return (
    <div className={`${styles.tileDetails} ${className || ''}`}>
      {/* Estimated date or Paused shown first for waitlists */}
      {pausedLabel && (
        <span
          className={`${styles.tileDetailValue} ${styles.tileDetailPaused}`}
        >
          {pausedLabel}
        </span>
      )}
      {estimatedDate && !pausedLabel && (
        <span className={styles.tileDetailValue}>{estimatedDate}</span>
      )}
      {breed && <span className={styles.tileDetailValue}>{breed}</span>}
      {genderLabel && (
        <span className={styles.tileDetailValue}>{genderLabel}</span>
      )}
      {locationLabel && (
        <span className={styles.tileDetailValue}>{locationLabel}</span>
      )}
      {children}
    </div>
  );
}

export type TileMetaProps = {
  /** Meta text to display */
  children: ReactNode;
  /** Additional class name */
  className?: string;
};

/**
 * TileMeta - Additional metadata line (date, author, etc.)
 */
export function TileMeta({ children, className }: TileMetaProps) {
  return <p className={`${styles.tileMeta} ${className || ''}`}>{children}</p>;
}
